import { useState } from "react";
import { Accordion } from "react-bootstrap";
import BookingsTable from "./BookingsTable";

/**
 * PostAccordionItem — single accordion entry for one post.
 *
 * Props:
 *   post             – { id, header, description, date, time, visible }
 *   bookings         – array of bookings for this post (or undefined)
 *   onToggleVisibility(id, newVisible) – callback to show/hide post
 *   onLoadBookings(id)                 – callback to fetch bookings
 *   onUpdatePost(id, updates)          – callback to edit post
 *   onDeletePost(id)                   – callback to delete post
 */
function PostAccordionItem({ post, bookings, onToggleVisibility, onLoadBookings, onUpdatePost, onDeletePost }) {
  const [bookingsOpen, setBookingsOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [header, setHeader] = useState(post.header);
  const [description, setDescription] = useState(post.description || "");
  const [date, setDate] = useState(post.date);
  const [time, setTime] = useState(post.time);

  const handleSave = async () => {
    await onUpdatePost(post.id, { header, description, date, time });
    setEditing(false);
  };

  const handleCancelEdit = () => {
    setHeader(post.header);
    setDescription(post.description || "");
    setDate(post.date);
    setTime(post.time);
    setEditing(false);
  };

  return (
    <Accordion.Item eventKey={String(post.id)}>
      <Accordion.Header>
        {post.header} — {post.date} kl. {post.time}
      </Accordion.Header>
      <Accordion.Body>
        {editing ? (
          /* ─── Edit mode ─── */
          <div className="card border-primary mb-3">
            <div className="card-body">
              <h6 className="fw-bold mb-3">✏️ Rediger konsultasjon</h6>
              <div className="mb-2">
                <label className="form-label">Overskrift</label>
                <input
                  className="form-control"
                  value={header}
                  onChange={(e) => setHeader(e.target.value)}
                  required
                />
              </div>
              <div className="mb-2">
                <label className="form-label">Beskrivelse</label>
                <textarea
                  className="form-control"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={2}
                />
              </div>
              <div className="row mb-3">
                <div className="col">
                  <label className="form-label">Dato</label>
                  <input
                    type="date"
                    className="form-control"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    required
                  />
                </div>
                <div className="col">
                  <label className="form-label">Tid</label>
                  <input
                    type="time"
                    className="form-control"
                    value={time}
                    onChange={(e) => setTime(e.target.value)}
                    required
                  />
                </div>
              </div>
              <div className="d-flex gap-2">
                <button className="btn btn-primary" onClick={handleSave}>
                  💾 Lagre
                </button>
                <button className="btn btn-secondary" onClick={handleCancelEdit}>
                  Avbryt
                </button>
              </div>
            </div>
          </div>
        ) : (
          /* ─── View mode ─── */
          <>
            <p>
              <strong>Beskrivelse:</strong> {post.description || "Ingen beskrivelse"}
            </p>
            <p>
              <strong>Status:</strong>{" "}
              <span className={`badge ${post.visible ? "bg-success" : "bg-secondary"}`}>
                {post.visible ? "Synlig" : "Skjult"}
              </span>
            </p>
          </>
        )}

        {/* Actions */}
        <div className="mb-3 d-flex flex-wrap gap-2">
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={() => onToggleVisibility(post.id, !post.visible)}
          >
            {post.visible ? "👁️‍🗨️ Skjul" : "👁️ Vis"}
          </button>
          {!editing && (
            <button
              className="btn btn-sm btn-outline-warning"
              onClick={() => setEditing(true)}
            >
              ✏️ Rediger
            </button>
          )}
          <button
            className="btn btn-sm btn-outline-danger"
            onClick={() => onDeletePost(post.id)}
          >
            🗑️ Slett
          </button>
          <button
            className="btn btn-sm btn-outline-info"
            onClick={() => { onLoadBookings(post.id); setBookingsOpen(o => !o); }}
          >
            Vis påmeldinger
          </button>
        </div>

        {bookingsOpen && (
          <div>
            <h5>Påmeldinger</h5>
            <BookingsTable bookings={bookings} />
          </div>
        )}
      </Accordion.Body>
    </Accordion.Item>
  );
}

export default PostAccordionItem;
